import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/storage'
import 'firebase/functions'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import React, { Component } from 'react'
import { Provider, connect } from 'react-redux'
import { ConnectedRouter } from 'react-router-redux'
import ReactGA from 'react-ga'
import { withTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import { withRouter } from 'react-router'
import { firebaseConnect, isEmpty } from 'react-redux-firebase'
import { ShortcutManager } from 'react-shortcuts'
import { lifecycle } from 'recompose'
import { replace } from 'react-router-redux'
import { compose } from 'redux'
import keymap from '../../keymap'
import { setBoardSize } from '../../store/actions/boards'
import {
  pendingUploadsSelector,
  embedModeSelector
} from '../../store/selectors/selectors'
import store from '../../store/store'
import {
  userIdSelector,
  profileSelector,
  authSelector
} from '../../store/selectors/baseSelectors'
import { history } from 'Root/history'
import App from './App'
import { sentryDsn } from 'variables'
import { gotMouseAction } from '../../store/actions/app'

// polyfills
import 'core-js/fn/object/values'
import 'core-js/fn/object/entries'
import 'intersection-observer'
import { showAlert } from '../../store/actions/alerts'

window.dumpStore = () => {
  var dataStr =
    'data:text/json;charset=utf-8,' +
    encodeURIComponent(JSON.stringify(store.getState()))
  var downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  downloadAnchorNode.setAttribute(
    'download',
    `storeDump_${new Date().toISOString()}.json`
  )
  document.body.appendChild(downloadAnchorNode) // required for firefox
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

// ========================================================
// Error logging to sentry
// ========================================================
if (process.env.NODE_ENV === 'production' || !process.env.REACT_APP_NO_SENTRY) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_CONFIG,
    release: process.env.REACT_APP_VERSION
  })
}

const shortcutManager = new ShortcutManager(keymap)

let lastTouch
const detectMouse = event => {
  if (event.type === 'touchend') lastTouch = new Date()
  if (event.type === 'mousemove') {
    // if there was a touch just now then this mousemove is probablly fake
    // and triggered by a touch action
    if (!lastTouch || new Date() - lastTouch > 60) {
      document.removeEventListener('mousemove', detectMouse)
      document.removeEventListener('touchend', detectMouse)
      store.dispatch(gotMouseAction())
    }
  }
}

const enhance = compose(
  withRouter, // stop react-redux blocking updates - https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/blocked-updates.md

  connect(state => {
    const auth = authSelector(state)
    const profile = profileSelector(state)
    return {
      userId: userIdSelector(state),
      pendingUploads: pendingUploadsSelector(state),
      // We don't directly use firebase but we have to pretend to be watching it so we rerender App when it changes
      // TODO, is this really needed, is there a better way?
      firebase: state.firebase,
      embedMode: embedModeSelector(state),
      mustAcceptTerms:
        !auth.isAnonymous && !isEmpty(profile) && !profile.termsAccepted
    }
  }),
  firebaseConnect(({ userId }) => {
    if (userId) {
      return [
        `/access/${userId}`,
        { path: `/queues/${userId}`, orderByKey: true },
        `/activity/feeds/${userId}/unreadCount`
      ]
    }
  }),
  withTranslation(),
  lifecycle({
    componentDidMount() {
      // send to accept terms if not yet done
      if (this.props.mustAcceptTerms) window.location.hash = '#terms'

      // Warn about pending uploads
      window.onbeforeunload = () =>
        this.props.pendingUploads
          ? this.props.t('alert.uploadsPending')
          : undefined

      document.addEventListener('touchend', detectMouse, false)
      document.addEventListener('mousemove', detectMouse, false)
    },
    componentDidUpdate() {
      // send to accept terms if not yet done
      if (this.props.mustAcceptTerms) window.location.hash = '#terms'
    },
    componentWillUnmount() {
      window.onbeforeunload = undefined
      document.removeEventListener('mousemove', detectMouse)
      document.removeEventListener('touchend', detectMouse)
    }
  })
)

const AppEnhanced = enhance(App)

let currentUser
export class AppContainer extends Component {
  getChildContext() {
    return { shortcuts: shortcutManager }
  }

  state = {}
  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    Sentry.withScope(scope => {
      Object.keys(info).forEach(key => {
        scope.setExtra(key, info[key])
      })
      Sentry.captureException(error)
    })

    store.dispatch(
      showAlert(null, i18n.t('error.message'), null, [
        {
          label: i18n.t('error.refresh'),
          action: () => window.location.reload()
        }
      ])
    )
  }

  async componentDidMount() {
    document.documentElement.classList.add('app')

    this.stopWatchingAuth = firebase.auth().onAuthStateChanged(user => {
      if (user) {
        console.info(
          'User ID:',
          user.uid,
          user.isAnonymous ? '(anonymous)' : '(' + user.email + ')'
        )

        // Set user details in Google Analytics
        if (!user.isAnonymous && process.env.NODE_ENV === 'production') {
          ReactGA.set({ userId: user.uid })
        }

        // Set user details in Sentry
        if (!user.isAnonymous && !process.env.REACT_APP_NO_SENTRY) {
          Sentry.configureScope(scope => {
            scope.setUser({
              email: user.email,
              id: user.uid,
              name: user.displayName
            })
          })
        }

        currentUser = user
      } else {
        // ensure there is an anon login at least
        firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION)
        firebase.auth().signInAnonymously()

        // Log out
        if (currentUser) {
          ReactGA.event({ category: 'User', action: 'Logout' }, [
            'app',
            'website'
          ])
          ReactGA.set({ userId: undefined })

          // redirect to website
          store.dispatch(replace('/'))
        }
      }
    })

    // keep window size updated in store
    window.addEventListener('resize', this.setBoardSize)
    this.setBoardSize()
  }

  componentWillUnmount() {
    this.stopWatchingAuth()
    document.documentElement.classList.remove('app')
    window.removeEventListener('resize', this.setBoardSize)
  }

  setBoardSize() {
    store.dispatch(
      setBoardSize({
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight
      })
    )
  }

  render() {
    return (
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <AppEnhanced hasError={this.state.hasError} />
        </ConnectedRouter>
      </Provider>
    )
  }
}

AppContainer.childContextTypes = {
  shortcuts: PropTypes.object.isRequired
}

export default AppContainer
